import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from "@tanstack/react-query";
import { tasksApi } from "./api";
import type { TaskQueryParams, SendMessageRequest, Task, TaskStatus } from "./types";

export function useTasks(params: TaskQueryParams = {}) {
  return useInfiniteQuery({
    queryKey: ["tasks", params],
    queryFn: ({ pageParam }) =>
      tasksApi.getTasks({ ...params, cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined as string | undefined,
  });
}

export function useTask(id: number, instanceId?: number) {
  return useQuery({
    queryKey: ["tasks", id, instanceId || "all"],
    queryFn: () => tasksApi.getTask(id, instanceId),
    enabled: !!id,
  });
}

export function useTaskMessages(taskId: number, instanceId?: number) {
  return useInfiniteQuery({
    queryKey: ["tasks", taskId, "messages", instanceId || "all"],
    queryFn: ({ pageParam }) =>
      tasksApi.getTaskMessages(taskId, pageParam, instanceId),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined as string | undefined,
    enabled: !!taskId,
    select: (data) => ({
      ...data,
      pages: data.pages.slice().reverse(), // Reverse for chronological order
    }),
  });
}

export function useSendMessage(taskId: number, instanceId?: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SendMessageRequest) =>
      tasksApi.sendMessage(taskId, data, instanceId),
    onMutate: async (data) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ["tasks", taskId, "messages"],
      });

      // Optimistic update
      const optimisticMessage = {
        id: Date.now(), // Temporary ID
        taskId,
        body: data.body,
        direction: "incoming" as const, // User messages are "incoming" from API perspective
        createdAt: new Date().toISOString(),
        senderName: "You",
      };

      queryClient.setQueryData(
        ["tasks", taskId, "messages"],
        (old: any) => {
          if (!old) return old;
          
          return {
            ...old,
            pages: old.pages.map((page: any, index: number) => {
              if (index === old.pages.length - 1) {
                return {
                  ...page,
                  data: [...page.data, optimisticMessage],
                };
              }
              return page;
            }),
          };
        }
      );

      return { optimisticMessage };
    },
    onError: (error, variables, context) => {
      // Rollback optimistic update
      if (context?.optimisticMessage) {
        queryClient.setQueryData(
          ["tasks", taskId, "messages"],
          (old: any) => {
            if (!old) return old;
            
            return {
              ...old,
              pages: old.pages.map((page: any, index: number) => {
                if (index === old.pages.length - 1) {
                  return {
                    ...page,
                    data: page.data.filter(
                      (msg: any) => msg.id !== context.optimisticMessage.id
                    ),
                  };
                }
                return page;
              }),
            };
          }
        );
      }
    },
    onSettled: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({
        queryKey: ["tasks", taskId, "messages"],
      });
      queryClient.invalidateQueries({
        queryKey: ["tasks"],
      });
    },
  });
}

export function useUpdateTaskStatus(taskId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: TaskStatus | { status: TaskStatus; comment?: string; instanceId?: number }) => {
      if (typeof params === 'string') {
        // Backward compatibility: if just a string is passed, treat it as status
        return tasksApi.updateTaskStatus(taskId, params);
      }
      return tasksApi.updateTaskStatus(taskId, params.status, params.comment, params.instanceId);
    },
    onSuccess: () => {
      // Invalidate and refetch all task-related queries
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return Array.isArray(queryKey) && queryKey[0] === 'tasks';
        }
      });
      // Also invalidate the specific task query
      queryClient.invalidateQueries({
        queryKey: ["task", taskId],
      });
    },
  });
}
